// apps/client/src/net/handlers/nature.ts (M1-T15) — an index of outdoor world objects for interactions (from the `out` snapshot and
// object.* events), the shake wobble and the falling-fruit animation.
import { getEntry } from '@whiskerton/content';
import type { ItemId, ObjectId, WorldObject } from '@whiskerton/shared';
import type { Game, NetHandlers } from '../../game/game';
import { useGame } from '../../state/store';
import { itemsLayerOf } from '../../world/items';
import { shakeTree } from '../../world/objects/tree';

const index = new WeakMap<object, Map<ObjectId, WorldObject>>();
export function objectsOf(game: Game | object): readonly WorldObject[] { return [...(index.get(game)?.values() ?? [])]; }
/** Internal (beyond the M1-T15 contract): seeds the index; used by onSpace and by tests. */
export function setObjectsFor(game: Game | object, objs: readonly WorldObject[]): void { index.set(game, new Map(objs.map((o) => [o.id, o]))); }
const mapOf = (game: object) => { let m = index.get(game); if (!m) { m = new Map(); index.set(game, m); } return m; };

const FALL_MS = 450, FALL_FROM = 2.2, BOUNCE = 0.06, FRUIT_REACH = 2.9;   // canopy height and the 5 × 5 drop ring (rules/fruit)
/** Fruit that just landed outdoors (item.added), so a shake drops only its own fruit, not what already lay under the tree. */
const RECENT_MS = 2000;
const recent = new WeakMap<object, Map<ItemId, number>>();
const recentOf = (game: object) => { let m = recent.get(game); if (!m) { m = new Map(); recent.set(game, m); } return m; };
/** The new fruit around the tree falls from the canopy: 450 ms, ease-in, a small bounce. The server sends `item.added` for the drops
 *  before `tree.shaken`, so their views are loading or loaded; one that appears mid-fall joins it. */
function dropFruit(game: Game, tree: WorldObject): void {
  const layer = itemsLayerOf(game);
  if (!layer) return;
  const start = performance.now(), fresh = recentOf(game);
  for (const [id, at] of fresh) if (start - at > RECENT_MS) fresh.delete(id);
  const cx = tree.x + 0.5, cy = tree.y + 0.5;
  const near = layer.items().filter((i) => fresh.has(i.id) && i.at.loc === 'outdoors' && !i.at.placed
    && Math.hypot(i.at.x + 0.5 - cx, i.at.y + 0.5 - cy) <= FRUIT_REACH);
  for (const i of near) fresh.delete(i.id);
  const step = () => {
    const t = Math.min(1, (performance.now() - start) / FALL_MS);
    for (const i of near) {
      const v = layer.viewOf(i.id);
      if (!v) continue;
      v.userData.groundY ??= v.position.y;
      const y0 = v.userData.groundY as number;
      v.position.y = y0 + (1 - t * t) * FALL_FROM + (t === 1 ? 0 : Math.max(0, Math.sin(t * Math.PI * 3)) * BOUNCE);
    }
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

export const handlers: NetHandlers = {
  onSpace(space, game) { if (space.kind === 'out') setObjectsFor(game, space.objects); },
  events: {
    'object.added': (e, game) => { mapOf(game).set(e.obj.id, e.obj); },
    'object.updated': (e, game) => { mapOf(game).set(e.obj.id, e.obj); },
    'object.removed': (e, game) => { mapOf(game).delete(e.id); },
    'item.added': (e, game) => {
      if (e.item.at.loc === 'outdoors' && !e.item.at.placed && getEntry(e.item.catalogId)?.kind === 'fruit') recentOf(game).set(e.item.id, performance.now());
    },
    'tree.shaken': (e, game) => {
      const mine = e.r === useGame.getState().you?.resident;
      if (!mine) { shakeTree(e.tree); game.avatarOf(e.r)?.play('shake'); }   // your own wobble and anim already ran optimistically
      game.sound.sfx('shake');
      const tree = mapOf(game).get(e.tree);
      if (tree) { dropFruit(game, tree); setTimeout(() => game.sound.sfx('fruitDrop'), FALL_MS - 30); }
    },
  },
};
